// web/src/rendering/components/leaves/chartCommon.ts
// Shared types + helpers for leaf chart components.
import { fmtNumber } from "../../../lib/formatters";
export const PALETTE = [
    "#7c3aed", "#10b981", "#3b82f6",
    "#f59e0b", "#ef4444", "#8b5cf6",
    "#14b8a6", "#ec4899",
];
/** Reserved colors for paired-series semantics. When a series.name matches
 *  one of these tokens (case-insensitive), its colour is fixed regardless of
 *  position in the series list. This keeps Before/After framing visually
 *  consistent across every chart in the product:
 *    - Current / Today / Baseline = blue
 *    - Proposed / Recommended / Target / After / Optimized = green
 *    - Worst / Stress = red
 *    - P50 / Median = blue (emphasised)
 *    - P10 / Downside = amber
 *    - P90 / Upside = teal
 */
export const SEMANTIC_COLORS = {
    current: "#3b82f6",
    today: "#3b82f6",
    baseline: "#64748b",
    proposed: "#10b981",
    recommended: "#10b981",
    target: "#10b981",
    after: "#10b981",
    optimized: "#10b981",
    "after optimization": "#10b981",
    worst: "#ef4444",
    stress: "#ef4444",
    p10: "#f59e0b",
    downside: "#f59e0b",
    p25: "#f59e0b",
    p50: "#3b82f6",
    median: "#3b82f6",
    p75: "#14b8a6",
    p90: "#14b8a6",
    upside: "#14b8a6",
};
export function colorForSeries(name, fallback) {
    if (!name)
        return fallback;
    const key = name.toLowerCase().trim();
    return SEMANTIC_COLORS[key] ?? fallback;
}
/** Format a numeric value per the chart's y_format prop. Falls back to
 *  fmtNumber when format is undefined or "number".
 */
export function fmtAxis(value, y_format, locale) {
    if (typeof value !== "number")
        return String(value);
    if (y_format === "currency") {
        // Compact $ formatting for large magnitudes; full precision for small.
        const abs = Math.abs(value);
        if (abs >= 1e6)
            return `$${(value / 1e6).toFixed(value % 1e6 === 0 ? 0 : 2)}M`;
        if (abs >= 1e3)
            return `$${(value / 1e3).toFixed(value % 1e3 === 0 ? 0 : 1)}k`;
        return `$${fmtNumber(value, locale)}`;
    }
    if (y_format === "percent") {
        // Heuristic: values below |2| are treated as decimal fractions and
        // converted to percent; values above are treated as already-percent.
        const pct = Math.abs(value) < 2 ? value * 100 : value;
        return `${pct.toFixed(Math.abs(pct) >= 10 ? 1 : 2)}%`;
    }
    return fmtNumber(value, locale);
}
/** Flatten series[{name, data:[{x,y}]}] into Recharts row shape.
 *  Defensive against malformed inputs: series missing `data`, or `data` not
 *  an array, are skipped rather than crashing the render.
 */
export function toRechartsRows(series) {
    if (!Array.isArray(series) || series.length === 0)
        return [];
    const safe = series.filter((s) => s && Array.isArray(s.data));
    if (safe.length === 0)
        return [];
    const xs = Array.from(new Set(safe.flatMap((s) => s.data.map((d) => d.x))));
    return xs.map((x) => {
        const row = { x };
        for (const s of safe) {
            const found = s.data.find((d) => d.x === x);
            row[s.name] = found?.y ?? null;
        }
        return row;
    });
}
export function strokeDashFor(style) {
    if (style === "dashed")
        return "6 3";
    if (style === "dotted")
        return "2 3";
    return undefined;
}
